import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, IconButton, Chip, LinearProgress, Button, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ReplayIcon from '@mui/icons-material/Replay';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import SourceToggle from './Benchmark/supplied/SourceToggle';
import SuppliedRunList from './Benchmark/supplied/SuppliedRunList';
import { useSuppliedRuns } from './Benchmark/supplied/useSuppliedRuns';
import AgentSelectionPicker from './Benchmark/AgentSelectionPicker';
import { dsVar, ink } from '../theme/tokens';

/** Open width. The panel animates between this and 0, squeezing the canvas. */
const PANEL_WIDTH = 400;

const STATUS_META = {
  idle: { label: 'Idle', color: '#94A3B8' },
  starting: { label: 'Starting', color: '#FFDE59' },
  running: { label: 'Running', color: '#FFDE59' },
  success: { label: 'Succeeded', color: '#10B981' },
  failure: { label: 'Failed', color: '#F87171' },
};

/**
 * Shown while the run has started but no reasoning step has arrived yet.
 *
 * These describe what the analysis pipeline does in general — they are NOT a
 * report of a verified backend step, because nothing is emitted before the first
 * reasoning log. Keep them generic for that reason; naming a specific dataset or
 * count here would be inventing status.
 */
const PIPELINE_STAGES = [
  'Ingesting the datasets…',
  'Profiling records and building features…',
  'Cross-checking against reference data…',
  'Running deep investigation routines…',
];

const STAGE_ROTATE_MS = 3000;

/** Cycles the pipeline copy while waiting on the first step. */
const RotatingStage = ({ active }) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => setIndex((i) => (i + 1) % PIPELINE_STAGES.length), STAGE_ROTATE_MS);
    return () => clearInterval(id);
  }, [active]);

  return (
    // Keyed so each line replays the entry animation as it swaps in.
    <Typography
      key={index}
      className="agent-run-mono agent-run-entry"
      sx={{ mt: 1 }}
    >
      {PIPELINE_STAGES[index]}
    </Typography>
  );
};

const formatElapsed = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
};

/**
 * Ticks once a second while the run is live so the header shows elapsed time.
 * `times` is `{ startedAt, endedAt? }` in epoch ms.
 */
const ElapsedTimer = ({ times, isLive }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!isLive || !times) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isLive, times]);

  if (!times?.startedAt) return null;
  return (
    <Typography sx={{ fontSize: '11px', color: dsVar('fg-muted'), fontVariantNumeric: 'tabular-nums' }}>
      {formatElapsed((isLive ? now : times.endedAt ?? now) - times.startedAt)}
    </Typography>
  );
};

/**
 * Presentational run panel. Rendered inline as a flex sibling of the ReactFlow
 * canvas rather than as an overlay Drawer, so the canvas — and its live node
 * highlighting — stays visible and interactive for the whole run.
 *
 * All run state, the websocket and the canvas mapping live in AgentFlowEditor;
 * `entries` arrive already decorated with the canvas node each one resolved to.
 */
const FlowRunPanel = ({
  open,
  onClose,
  status = 'idle',
  entries = [],
  branches = [],
  selectedBranch,
  onSelectBranch,
  branchLabelFor,
  error,
  onDismissError,
  times,
  operatorName,
  onViewResults,
  latestRun = null,
  onReRunTest,
  isCheckingRun = false,
  // Supplied samples: the families this workflow's runs are recorded under, the
  // project they belong to, and a counter the editor bumps when one completes.
  suppliedFamilies = [],
  projectId = null,
  suppliedRunsVersion = 0,
  onBenchmarkSample,
  // The results button's wording, when the run just made is a supplied sample.
  viewResultsLabel,
  // Why a supplied sample finished without a run: { reason, advice, preview, failedStep }.
  suppliedNotScored = null,
  onResubmitSample,
  // The benchmark flow a re-run starts, whose agent catalogue the picker offers. A flow
  // with no supervised investigation has none, and the picker shows nothing.
  benchmarkFlowCode = '',
}) => {
  const endRef = useRef(null);
  const [source, setSource] = useState('mined');
  // The agents a re-run uses; null runs every agent the flow has.
  const [agentSelection, setAgentSelection] = useState(null);
  const [mineOnly, setMineOnly] = useState(false);
  const supplied = useSuppliedRuns({
    families: suppliedFamilies,
    projectId,
    mineOnly,
    enabled: open && source === 'user_supplied',
  });
  const { loaded: suppliedLoaded, reload: reloadSupplied } = supplied;

  // A supplied run just finished: refresh the list if it has been shown, and
  // switch to it so the new run is in view.
  useEffect(() => {
    if (!suppliedRunsVersion) return;
    setSource('user_supplied');
    if (suppliedLoaded) reloadSupplied();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suppliedRunsVersion]);
  const isLive = status === 'starting' || status === 'running';
  const meta = STATUS_META[status] || STATUS_META.idle;

  const visible = selectedBranch
    ? entries.filter((e) => (e.branchKey || '') === selectedBranch)
    : entries;

  // Guarded on `open`: unlike the Drawer this replaced, the inline panel keeps
  // its content mounted when collapsed, and scrolling a zero-width hidden
  // element into view drags an ancestor instead.
  useEffect(() => {
    if (!open) return;
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [visible.length, open]);

  return (
    <Box
      sx={{
        width: open ? PANEL_WIDTH : 0,
        flexShrink: 0,
        overflow: 'hidden',
        transition: 'width 0.25s ease',
        borderLeft: open ? `1px solid ${dsVar('line')}` : 'none',
        backgroundColor: dsVar('surface'),
        color: dsVar('fg-body'),
      }}
    >
      {/* Fixed inner width: without it the text re-wraps on every frame of the
          width transition. */}
      <Box sx={{ width: PANEL_WIDTH, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* ── Header ── */}
        <Box sx={{ p: 2.5, borderBottom: `1px solid ${dsVar('line')}`, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 700, color: dsVar('fg') }}>
                Backtest
              </Typography>
              <Chip
                label={!isLive && latestRun ? `Run #${latestRun.runKey}` : meta.label}
                size="small"
                sx={{
                  height: '20px',
                  fontSize: '10px',
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  color: !isLive && latestRun ? ink('#38BDF8') : meta.color,
                  backgroundColor: !isLive && latestRun ? 'rgba(56, 189, 248, 0.15)' : `${meta.color}22`,
                  border: `1px solid ${!isLive && latestRun ? 'rgba(56, 189, 248, 0.35)' : `${meta.color}55`}`,
                }}
              />
              <ElapsedTimer times={times} isLive={isLive} />
            </Box>
            <IconButton onClick={onClose} size="small" sx={{ color: dsVar('fg-2') }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          {operatorName && (
            <Typography sx={{ mt: 0.5, fontSize: '12px', color: dsVar('fg-2'), wordBreak: 'break-word' }}>
              {operatorName}
            </Typography>
          )}

          {/* Branch filter — only meaningful once a fan-out has produced lanes. */}
          {branches.length > 1 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5 }}>
              <Chip
                label="All"
                size="small"
                onClick={() => onSelectBranch?.(null)}
                sx={chipSx(!selectedBranch)}
              />
              {branches.map((key, index) => (
                <Chip
                  key={key}
                  label={branchLabelFor ? branchLabelFor(key, index) : key}
                  size="small"
                  onClick={() => onSelectBranch?.(key)}
                  sx={chipSx(selectedBranch === key)}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* ── Error ── */}
        {error && (
          <Box
            sx={{
              m: 2,
              p: 1.5,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.25,
              borderRadius: '12px',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              flexShrink: 0,
            }}
          >
            <ErrorOutlineIcon sx={{ color: ink('#F87171'), fontSize: 20, mt: '2px' }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 700, color: ink('#FCA5A5') }}>
                Run couldn't complete
              </Typography>
              <Typography sx={{ fontSize: '12px', color: 'rgba(254, 202, 202, 0.8)', mt: 0.25 }}>
                {error}
              </Typography>
            </Box>
            <IconButton onClick={onDismissError} size="small" sx={{ color: 'rgba(252, 165, 165, 0.6)' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        {/* ── A supplied sample that was not scored, and why ── */}
        {suppliedNotScored && (
          <Box
            role="alert"
            sx={{
              m: 2,
              p: 1.75,
              borderRadius: '12px',
              backgroundColor: suppliedNotScored.preview ? dsVar('fg-2', 0.08) : 'rgba(239, 68, 68, 0.1)',
              border: `1px solid ${suppliedNotScored.preview ? dsVar('fg-2', 0.3) : 'rgba(239, 68, 68, 0.3)'}`,
              flexShrink: 0,
            }}
          >
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: suppliedNotScored.preview ? dsVar('fg-soft') : ink('#FCA5A5') }}>
              {suppliedNotScored.preview ? 'This was a preview — nothing was scored' : 'Your sample was not scored'}
            </Typography>
            {suppliedNotScored.reason ? (
              <Typography
                sx={{
                  fontSize: '12px', color: 'rgba(254, 202, 202, 0.9)', mt: 0.75,
                  fontFamily: '"JetBrains Mono", monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                }}
              >
                {suppliedNotScored.reason}
              </Typography>
            ) : !suppliedNotScored.preview ? (
              <Typography sx={{ fontSize: '12px', color: 'rgba(254, 202, 202, 0.8)', mt: 0.5 }}>
                The run finished without writing results, and no reason was recorded.
              </Typography>
            ) : null}
            {suppliedNotScored.advice && (
              <Typography sx={{ fontSize: '12px', color: dsVar('fg-soft'), mt: 0.75, lineHeight: 1.5 }}>
                {suppliedNotScored.advice}
              </Typography>
            )}
            {suppliedNotScored.failedStep && (
              <Typography sx={{ fontSize: '11px', color: dsVar('fg-muted'), mt: 0.5 }}>
                Stopped at: {suppliedNotScored.failedStep}
              </Typography>
            )}
            {onResubmitSample && (
              <Button
                size="small"
                onClick={onResubmitSample}
                startIcon={<ReplayIcon sx={{ fontSize: 16 }} />}
                sx={{ mt: 1, textTransform: 'none', fontWeight: 700, color: dsVar('accent-fg'), px: 1 }}
              >
                Fix and resubmit
              </Button>
            )}
          </Box>
        )}

        {/* ── Timeline ── */}
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2.5 }}>
          {visible.length === 0 ? (
            isCheckingRun ? (
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  textAlign: 'center',
                  p: 3,
                }}
              >
                <CircularProgress size={32} sx={{ color: dsVar('accent-fg') }} />
                <Typography sx={{ fontSize: '13px', color: dsVar('fg-2') }}>
                  Checking benchmark test records…
                </Typography>
              </Box>
            ) : isLive ? (
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2.5,
                  textAlign: 'center',
                }}
              >
                <Box sx={{ width: '70%' }}>
                  {/* Indeterminate: the wait has no knowable duration, and a fake
                      percentage would be the one dishonest thing on this screen. */}
                  <LinearProgress
                    sx={{
                      height: 3,
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 222, 89, 0.12)',
                      '& .MuiLinearProgress-bar': { backgroundColor: dsVar('accent') },
                    }}
                  />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '13px', fontWeight: 700, color: dsVar('fg') }}>
                    {status === 'starting'
                      ? 'Connecting to the analysis engine'
                      : 'Analysing your data'}
                  </Typography>
                  <Box sx={{ maxWidth: '260px' }}>
                    {status === 'starting' ? (
                      <Typography className="agent-run-mono" sx={{ mt: 1 }}>
                        Handing your workflow to the agent runtime…
                      </Typography>
                    ) : (
                      <RotatingStage active />
                    )}
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minHeight: '100%' }}>
                <SourceToggle value={source} onChange={setSource} fullWidth />
                {source === 'user_supplied' ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Typography sx={{ fontSize: '11px', color: dsVar('fg-2'), lineHeight: 1.5 }}>
                      Runs over alerts someone supplied rather than your institution's history. Each scores a
                      different sample, so pick the one you want to open.
                    </Typography>
                    {onBenchmarkSample && (
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={onBenchmarkSample}
                        startIcon={<UploadFileOutlinedIcon />}
                        sx={benchmarkSampleButtonSx}
                      >
                        Benchmark a sample
                      </Button>
                    )}
                    <SuppliedRunList
                      runs={supplied.runs}
                      loading={supplied.loading}
                      error={supplied.error}
                      onRetry={supplied.reload}
                      onSelect={(run) => onViewResults && onViewResults(run.runKey)}
                      mineOnly={mineOnly}
                      onMineOnlyChange={setMineOnly}
                    />
                  </Box>
                ) : latestRun ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {/* Existing Run Details Card */}
                <Box
                  sx={{
                    p: 2.25,
                    borderRadius: '12px',
                    backgroundColor: dsVar('page', 0.85),
                    border: `1px solid ${dsVar('line')}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon sx={{ color: ink('#10B981'), fontSize: 20 }} />
                      <Typography sx={{ fontSize: '13px', fontWeight: 700, color: dsVar('fg') }}>
                        Latest Benchmark Evaluation
                      </Typography>
                    </Box>
                    <Chip
                      label={`Run #${latestRun.runKey}`}
                      size="small"
                      sx={{
                        height: '20px',
                        fontSize: '10px',
                        fontWeight: 700,
                        color: ink('#38BDF8'),
                        backgroundColor: 'rgba(56, 189, 248, 0.12)',
                        border: '1px solid rgba(56, 189, 248, 0.3)',
                      }}
                    />
                  </Box>

                  <Typography sx={{ fontSize: '12px', color: dsVar('fg-2'), mb: 2, lineHeight: 1.5 }}>
                    A benchmark test run matching this workflow, project, and organization is available.
                  </Typography>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 1.5,
                      backgroundColor: dsVar('page'),
                      p: 1.5,
                      borderRadius: '8px',
                      border: `1px solid ${dsVar('fg', 0.06)}`,
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontSize: '10px', color: dsVar('fg-muted'), textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Run ID
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '11px',
                          color: dsVar('fg-soft'),
                          fontFamily: '"JetBrains Mono", monospace',
                          fontWeight: 600,
                          wordBreak: 'break-all',
                        }}
                      >
                        {latestRun.runId}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '10px', color: dsVar('fg-muted'), textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Alerts Evaluated
                      </Typography>
                      <Typography sx={{ fontSize: '13px', color: dsVar('accent-fg'), fontWeight: 700 }}>
                        {/* No stand-in count: a run that recorded none says so. */}
                        {Number.isFinite(Number(latestRun.predictionCount))
                          ? Number(latestRun.predictionCount).toLocaleString()
                          : '—'}
                      </Typography>
                    </Box>
                    <Box sx={{ gridColumn: 'span 2' }}>
                      <Typography sx={{ fontSize: '10px', color: dsVar('fg-muted'), textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Completed
                      </Typography>
                      <Typography sx={{ fontSize: '11px', color: dsVar('fg-2') }}>
                        {latestRun.completedAt
                          ? new Date(latestRun.completedAt).toLocaleString(undefined, {
                              dateStyle: 'medium',
                              timeStyle: 'short',
                            })
                          : 'Prior test run'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Option 1: Re-run the backtest */}
                <Box
                  sx={{
                    p: 2,
                    borderRadius: '10px',
                    backgroundColor: 'rgba(56, 189, 248, 0.04)',
                    border: '1px solid rgba(56, 189, 248, 0.25)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.25,
                  }}
                >
                  <Box>
                    <Typography sx={{ fontSize: '12px', fontWeight: 700, color: ink('#38BDF8'), letterSpacing: '0.02em' }}>
                      Option 1 &bull; Re-run backtest
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: dsVar('fg-2'), mt: 0.25 }}>
                      Re-execute the flow with benchmark dataset and stream reasoning steps live.
                    </Typography>
                  </Box>
                  {benchmarkFlowCode && (
                    <AgentSelectionPicker
                      flowCode={benchmarkFlowCode}
                      value={agentSelection}
                      onChange={setAgentSelection}
                      dense
                    />
                  )}
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => onReRunTest && onReRunTest(agentSelection)}
                    startIcon={<PlayArrowIcon />}
                    sx={{
                      color: ink('#38BDF8'),
                      borderColor: '#38BDF8',
                      fontWeight: 600,
                      textTransform: 'none',
                      borderRadius: '6px',
                      py: 0.85,
                      '&:hover': {
                        borderColor: '#7DD3FC',
                        backgroundColor: 'rgba(56, 189, 248, 0.1)',
                      },
                    }}
                  >
                    Re-run backtest
                  </Button>
                </Box>

                {/* Option 2: Benchmark a sample the user supplies */}
                {onBenchmarkSample && (
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: '10px',
                      backgroundColor: 'rgba(167, 139, 250, 0.05)',
                      border: '1px solid rgba(167, 139, 250, 0.3)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.25,
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontSize: '12px', fontWeight: 700, color: ink('#C4B5FD'), letterSpacing: '0.02em' }}>
                        Option 2 &bull; Benchmark a sample
                      </Typography>
                      <Typography sx={{ fontSize: '11px', color: dsVar('fg-2'), mt: 0.25 }}>
                        Upload your own closed alerts (CSV, JSON or JSONL), and optionally their customers' KYC.
                        Preview how the file is read, then run it.
                      </Typography>
                    </Box>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={onBenchmarkSample}
                      startIcon={<UploadFileOutlinedIcon />}
                      sx={benchmarkSampleButtonSx}
                    >
                      Benchmark a sample
                    </Button>
                  </Box>
                )}

                {/* Option 3: View Benchmark Results */}
                <Box
                  sx={{
                    p: 2,
                    borderRadius: '10px',
                    backgroundColor: 'rgba(255, 222, 89, 0.05)',
                    border: '1px solid rgba(255, 222, 89, 0.25)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.25,
                  }}
                >
                  <Box>
                    <Typography sx={{ fontSize: '12px', fontWeight: 700, color: dsVar('accent-fg'), letterSpacing: '0.02em' }}>
                      Option {onBenchmarkSample ? 3 : 2} &bull; View Benchmark Results
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: dsVar('fg-2'), mt: 0.25 }}>
                      Open the full diagnostic dashboard with executive KPIs, triage simulator, and rescued alerts.
                    </Typography>
                  </Box>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => onViewResults && onViewResults()}
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      backgroundColor: '#FFD600',
                      color: dsVar('on-accent'),
                      fontWeight: 700,
                      textTransform: 'none',
                      borderRadius: '6px',
                      py: 0.95,
                      '&:hover': { backgroundColor: '#E6C100' },
                    }}
                  >
                    View Benchmark Results &rarr;
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  textAlign: 'center',
                }}
              >
                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: dsVar('fg') }}>
                  No activity yet
                </Typography>
                <Typography sx={{ fontSize: '11px', color: dsVar('fg-muted'), maxWidth: '260px' }}>
                  Run this workflow to see its reasoning steps here.
                </Typography>
                {onReRunTest && (
                  <Button
                    variant="outlined"
                    onClick={onReRunTest}
                    startIcon={<PlayArrowIcon />}
                    sx={{
                      mt: 1,
                      color: dsVar('accent-fg'),
                      borderColor: dsVar('accent-fg'),
                      textTransform: 'none',
                      fontSize: '12px',
                      '&:hover': { backgroundColor: 'rgba(255,222,89,0.1)' },
                    }}
                  >
                    Start backtest
                  </Button>
                )}
                {onBenchmarkSample && (
                  <Button
                    variant="outlined"
                    onClick={onBenchmarkSample}
                    startIcon={<UploadFileOutlinedIcon />}
                    sx={{ ...benchmarkSampleButtonSx, fontSize: '12px' }}
                  >
                    Benchmark a sample
                  </Button>
                )}
              </Box>
            )}
              </Box>
            )
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {visible.map((entry, index) => {
                const isLast = index === visible.length - 1;
                const isActive = isLive && isLast;
                return (
                  // Keyed on the entry's position in the *unfiltered* list so
                  // toggling the branch filter doesn't re-key rows and replay
                  // their entry animation.
                  <Box
                    key={entry.sourceIndex ?? index}
                    className="agent-run-entry"
                    sx={{ display: 'flex', gap: 1.5, pl: entry.isRollup ? 2.5 : 0 }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          fontSize: '11px',
                          fontWeight: 700,
                          color: isActive ? dsVar('accent-fg') : ink('#10B981'),
                          backgroundColor: isActive ? 'rgba(255,222,89,0.1)' : 'rgba(16,185,129,0.1)',
                          border: `1px solid ${isActive ? 'rgba(255,222,89,0.25)' : 'rgba(16,185,129,0.25)'}`,
                        }}
                      >
                        {isActive ? '•' : '✓'}
                      </Box>
                      {!isLast && (
                        <Box sx={{ width: '1px', flex: 1, backgroundColor: dsVar('line') }} />
                      )}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {entry.activityTemplateName && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          {entry.isRollup && (
                            <AccountTreeIcon sx={{ fontSize: 13, color: ink('#A78BFA'), flexShrink: 0 }} />
                          )}
                          <Typography className="agent-run-step" sx={{ wordBreak: 'break-word' }}>
                            {entry.activityTemplateName}
                          </Typography>
                        </Box>
                      )}
                      {/* Size/colour/family come from .agent-run-mono in the
                          editor's stylesheet — an unscoped .MuiTypography-root
                          !important rule there outranks anything set via sx.
                          #94A3B8 holds ~6.2:1 on #112240 (AA for small text);
                          don't dim past it — #64748B drops to ~3.3:1. */}
                      {entry.reasoning && (
                        <Typography className="agent-run-mono" sx={{ mt: 0.4, wordBreak: 'break-word' }}>
                          {entry.reasoning}
                          {isActive && <span className="agent-run-caret" />}
                        </Typography>
                      )}
                      {entry.isRollup && entry.nodeName && (
                        <Typography sx={{ fontSize: '9px', color: dsVar('fg-muted'), mt: 0.5, letterSpacing: '0.08em' }}>
                          IN {entry.nodeName.toUpperCase()}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
              <div ref={endRef} />
            </Box>
          )}
        </Box>

        {/* ── Footer ── */}
        {status === 'success' && (
          <Box sx={{ p: 2, borderTop: `1px solid ${dsVar('line')}`, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {onViewResults && (
              <Button
                fullWidth
                onClick={() => onViewResults && onViewResults()}
                sx={{
                  backgroundColor: '#FFD600',
                  color: dsVar('on-accent'),
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: '8px',
                  py: 1,
                  '&:hover': { backgroundColor: '#E6C100' },
                }}
              >
                {viewResultsLabel ? <>{viewResultsLabel} &rarr;</> : <>View Benchmark Results &amp; Drill-Down &rarr;</>}
              </Button>
            )}
            {onReRunTest && (
              <Button
                fullWidth
                variant="outlined"
                onClick={onReRunTest}
                startIcon={<PlayArrowIcon />}
                sx={{
                  color: dsVar('fg-2'),
                  borderColor: dsVar('line'),
                  textTransform: 'none',
                  fontSize: '12px',
                  borderRadius: '8px',
                  '&:hover': { color: dsVar('accent-fg'), borderColor: dsVar('accent-fg') },
                }}
              >
                Re-run backtest
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const benchmarkSampleButtonSx = {
  color: '#C4B5FD',
  borderColor: 'rgba(167, 139, 250, 0.6)',
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '6px',
  py: 0.85,
  '&:hover': { borderColor: '#C4B5FD', backgroundColor: 'rgba(167, 139, 250, 0.1)' },
};

const chipSx = (selected) => ({
  height: '22px',
  fontSize: '10px',
  fontWeight: 700,
  cursor: 'pointer',
  color: selected ? dsVar('on-accent') : dsVar('fg-2'),
  backgroundColor: selected ? dsVar('accent') : dsVar('fg-2', 0.12),
  border: `1px solid ${selected ? dsVar('accent-fg') : dsVar('line')}`,
  '&:hover': { backgroundColor: selected ? dsVar('accent') : dsVar('fg-2', 0.2) },
});

export default FlowRunPanel;
